import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LoadResponse } from "../../../../models/api/load";
import { EmployeeGroupDTO } from "../../../../models/employee-groups";
import { TnACalendarRef, TnAEmployeeConfigDTO } from "../../../../models/tna";
import { VACATION_ADDED_ON_BEGINNING_OF_THE_YEAR_ID } from "../../../../utils/constants/tna-constants";
import { convertBooleanToYesOrNo } from "../../../../utils/convert-boolean-to-yes-or-no";
import { exportTnAToExcel } from "../../../../utils/export-presence-and-leaves";
import { getNormaleDateOrMonth, intlFormatDateShort, intlFormatDateWithHour } from "../../../../utils/time-utils";
import { monthsValues } from "../../../CustomDropdowns/CustomDropdownMonthsValues/CustomDropdownMonthsValues";
import ExportToExcelDialog from "../../ExportToExcelDialog/ExportToExcelDialog";
import { MyTnAContext } from "../../MyTeamPresenceAndLeaves";
import { excelStyles } from "../../utils";

interface Props {
  disabled: boolean;
  loadEmployeeGroup: () => Promise<EmployeeGroupDTO | undefined>;
  loadTnARulesData: (pageSize?: number) => Promise<LoadResponse<TnAEmployeeConfigDTO> | undefined>;
  allItems: () => TnAEmployeeConfigDTO[];
}

const ExportTnARulesToExcel = ({ disabled, loadEmployeeGroup, loadTnARulesData, allItems }: Props) => {
  const { t } = useTranslation();
  const tRP = "pages.system_settings.tables.tna_employee_config"; // tRP - translation root path
  const tRPTnAFields = "components.tna_config"; // tRP - translation root path for tna fields
  const tRPTnAConfig = "components.tna_config"; // tRPTnAConfig - translation root path TnAConfig
  const tRPCal = "pages.system_settings.tables.tna_calendars";
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const {
    legalEntity: { legalEntityOption, selectedLegalEntity },
    employeeGroup: { employeeGroupOption },
    currentEmployees: { onlyCurrentEmployeesOptionValue },
  } = useContext(MyTnAContext);

  const rowsBeforeHeaderInExcel = useCallback(
    (employeeGroup: EmployeeGroupDTO | undefined) => {
      return [
        {
          data: ["Rules"],
          fontSize: 16,
          isBold: true,
        },
        {
          data: [`Report date ${intlFormatDateWithHour(new Date())}`],
          fontSize: 14,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Legal entities: ${!legalEntityOption?.legalEntityIds?.length ? "All" : selectedLegalEntity?.name ?? ""}`,
          ],
          fontSize: 16,
          isBold: true,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Employee group: ${
              !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
            }`,
          ],
          fontSize: 12,
          isBold: false,
        },
        {
          data: [
            `Employee status: ${
              onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees ? "Only current employees" : "All employees"
            }`,
          ],
          fontSize: 12,
          isBold: false,
        },
      ];
    },
    [legalEntityOption?.legalEntityIds, selectedLegalEntity?.name, employeeGroupOption, onlyCurrentEmployeesOptionValue]
  );

  const headerNames = useMemo(() => {
    return [
      `${t(`${tRPCommon}.employee_code`)}`,
      `${t(`${tRPCommon}.internal_id`)}`,
      `${t("common_words.employee")}`,
      `${t(`${tRPCommon}.birth_date`)}`,
      `${t("common_words.legal_entity")}`,
      `${t("common_words.tna_employee_group")}`,
      `${t(`${tRP}.fields.tna_config_type`)}`,
      `${t(`${tRPTnAFields}.how_2_report_time`)}`,
      `${t(`${tRPTnAFields}.schedule`)}`,
      `${t(`${tRPTnAFields}.employee_can_claim_extra_time`)}`,
      `${t(`${tRPTnAFields}.employee_can_report_billable_time`)}`,
      `${t(`${tRPTnAFields}.public_holidays_calendars`)}`,
      `${t(`${tRPTnAFields}.vacation_days_annual_allowance`)}`,
      `${t(`${tRPTnAFields}.vacation_includes_weekends`)}`,
      `${t(`${tRPTnAFields}.vacation_excludes_offSchedule`)}`,
      `${t(`${tRPTnAFields}.vacation_days_added_at`)}`,
      `${t(`${tRPTnAConfig}.highlight_when_worked_time_more`)}`,
      `${t(`${tRPTnAConfig}.highlight_when_vacation_balance_more`)}`,
      `${t(`${tRPTnAConfig}.vacation_balance_after_date`)}`,
      `${t(`${tRPTnAConfig}.highlight_when_delay_in_signoff_more`)}`,
      `${t(`${tRPTnAConfig}.highlight_when_delay_in_signoff_reminders_more`)}`,
    ];
  }, []);

  const dataToExportAsExcel = useCallback(
    (allItems: TnAEmployeeConfigDTO[]) => {
      return allItems.map((item) => {
        return {
          [`${t(`${tRPCommon}.employee_code`)}`]: { value: item.employee?.employeeCode ?? " " },
          [`${t(`${tRPCommon}.internal_id`)}`]: { value: item.employee?.internalId ?? " " },
          [`${t("common_words.employee")}`]: {
            value: item.employee?.name ?? " ",
            style: { ...excelStyles.employeeCellStyle },
          },
          [`${t(`${tRPCommon}.birth_date`)}`]: {
            value: item.employee?.birthDate ? intlFormatDateShort(item.employee?.birthDate) : " ",
          },
          [`${t("common_words.legal_entity")}`]: { value: item.employee?.legalEntity?.name ?? " " },
          [`${t("common_words.tna_employee_group")}`]: { value: item?.employee?.tnAGroup?.name ?? " " },

          [`${t(`${tRP}.fields.tna_config_type`)}`]: {
            value: item.tnAConfig
              ? "Employee"
              : item.employeeGroupTnAConfig
              ? "Group"
              : item.legalEntityTnAConfig
              ? "Legal entity"
              : item.companyAccountTnAConfig
              ? "Company account"
              : item.legislationTnAConfig
              ? "Legislation"
              : item.allLegislationsTnAConfig
              ? "All legislations"
              : "None",
          },

          [`${t(`${tRPTnAFields}.how_2_report_time`)}`]: {
            value: item?.tnAConfig
              ? item.tnAConfig?.how2ReportTime?.name
              : item?.employeeGroupTnAConfig
              ? item.employeeGroupTnAConfig?.how2ReportTime?.name
              : item?.legalEntityTnAConfig
              ? item.legalEntityTnAConfig?.how2ReportTime?.name
              : item?.companyAccountTnAConfig
              ? item.companyAccountTnAConfig?.how2ReportTime?.name
              : item?.legislationTnAConfig
              ? item.legislationTnAConfig?.how2ReportTime?.name
              : item?.allLegislationsTnAConfig
              ? item.allLegislationsTnAConfig?.how2ReportTime?.name
              : " ",
          },

          [`${t(`${tRPTnAFields}.schedule`)}`]: {
            value: item?.tnAConfig
              ? item.tnAConfig?.schedule?.name
              : item?.employeeGroupTnAConfig
              ? item.employeeGroupTnAConfig?.schedule?.name
              : item?.legalEntityTnAConfig
              ? item.legalEntityTnAConfig?.schedule?.name
              : item?.companyAccountTnAConfig
              ? item.companyAccountTnAConfig?.schedule?.name
              : item?.legislationTnAConfig
              ? item.legislationTnAConfig?.schedule?.name
              : item?.allLegislationsTnAConfig
              ? item.allLegislationsTnAConfig?.schedule?.name
              : " ",
          },

          [`${t(`${tRPTnAFields}.employee_can_claim_extra_time`)}`]: {
            value: item?.tnAConfig
              ? convertBooleanToYesOrNo(item.tnAConfig["employeeCanClaimExtraTime"])
              : item?.employeeGroupTnAConfig
              ? convertBooleanToYesOrNo(item.employeeGroupTnAConfig["employeeCanClaimExtraTime"])
              : item?.legalEntityTnAConfig
              ? convertBooleanToYesOrNo(item.legalEntityTnAConfig["employeeCanClaimExtraTime"])
              : item?.companyAccountTnAConfig
              ? convertBooleanToYesOrNo(item.companyAccountTnAConfig["employeeCanClaimExtraTime"])
              : item?.legislationTnAConfig
              ? convertBooleanToYesOrNo(item.legislationTnAConfig["employeeCanClaimExtraTime"])
              : item?.allLegislationsTnAConfig
              ? convertBooleanToYesOrNo(item.allLegislationsTnAConfig["employeeCanClaimExtraTime"])
              : " ",
          },

          [`${t(`${tRPTnAFields}.employee_can_report_billable_time`)}`]: {
            value: item?.tnAConfig
              ? item.tnAConfig["billableTimeType"]?.name ?? "Not configured"
              : item?.employeeGroupTnAConfig
              ? item.employeeGroupTnAConfig["billableTimeType"]?.name ?? "Not configured"
              : item?.legalEntityTnAConfig
              ? item.legalEntityTnAConfig["billableTimeType"]?.name ?? "Not configured"
              : item?.companyAccountTnAConfig
              ? item.companyAccountTnAConfig["billableTimeType"]?.name ?? "Not configured"
              : item?.legislationTnAConfig
              ? item.legislationTnAConfig["billableTimeType"]?.name ?? "Not configured"
              : item?.allLegislationsTnAConfig
              ? item.allLegislationsTnAConfig["billableTimeType"]?.name ?? "Not configured"
              : " ",
          },

          [`${t(`${tRPTnAFields}.public_holidays_calendars`)}`]: {
            value: item?.tnAConfig
              ? item.tnAConfig?.publicHolidaysCalendars?.map((c: TnACalendarRef) => c.name).join(", ")
              : item?.employeeGroupTnAConfig
              ? item.employeeGroupTnAConfig?.publicHolidaysCalendars?.map((c: TnACalendarRef) => c.name).join(", ")
              : item?.legalEntityTnAConfig
              ? item.legalEntityTnAConfig?.publicHolidaysCalendars?.map((c: TnACalendarRef) => c.name).join(", ")
              : item?.companyAccountTnAConfig
              ? item.companyAccountTnAConfig?.publicHolidaysCalendars?.map((c: TnACalendarRef) => c.name).join(", ")
              : item?.legislationTnAConfig
              ? item.legislationTnAConfig?.publicHolidaysCalendars?.map((c: TnACalendarRef) => c.name).join(", ")
              : item?.allLegislationsTnAConfig
              ? item.allLegislationsTnAConfig?.publicHolidaysCalendars?.map((c: TnACalendarRef) => c.name).join(", ")
              : " ",
          },

          [`${t(`${tRPTnAFields}.vacation_days_annual_allowance`)}`]: {
            value: Number(
              item.tnAConfig?.vacationDaysAnnualAllowance ??
                item.employeeGroupTnAConfig?.vacationDaysAnnualAllowance ??
                item.legalEntityTnAConfig?.vacationDaysAnnualAllowance ??
                item.companyAccountTnAConfig?.vacationDaysAnnualAllowance ??
                item.legislationTnAConfig?.vacationDaysAnnualAllowance ??
                item.allLegislationsTnAConfig?.vacationDaysAnnualAllowance ??
                0
            ).toFixed(2),
          },

          [`${t(`${tRPTnAFields}.vacation_includes_weekends`)}`]: {
            value: item?.tnAConfig
              ? convertBooleanToYesOrNo(item.tnAConfig["vacationIncludesWeekends"])
              : item?.employeeGroupTnAConfig
              ? convertBooleanToYesOrNo(item.employeeGroupTnAConfig["vacationIncludesWeekends"])
              : item?.legalEntityTnAConfig
              ? convertBooleanToYesOrNo(item.legalEntityTnAConfig["vacationIncludesWeekends"])
              : item?.companyAccountTnAConfig
              ? convertBooleanToYesOrNo(item.companyAccountTnAConfig["vacationIncludesWeekends"])
              : item?.legislationTnAConfig
              ? convertBooleanToYesOrNo(item.legislationTnAConfig["vacationIncludesWeekends"])
              : item?.allLegislationsTnAConfig
              ? convertBooleanToYesOrNo(item.allLegislationsTnAConfig["vacationIncludesWeekends"])
              : " ",
          },

          [`${t(`${tRPTnAFields}.vacation_excludes_offSchedule`)}`]: {
            value: item?.tnAConfig
              ? convertBooleanToYesOrNo(item.tnAConfig["vacationExcludesOffScheduleWorkDays"])
              : item?.employeeGroupTnAConfig
              ? convertBooleanToYesOrNo(item.employeeGroupTnAConfig["vacationExcludesOffScheduleWorkDays"])
              : item?.legalEntityTnAConfig
              ? convertBooleanToYesOrNo(item.legalEntityTnAConfig["vacationExcludesOffScheduleWorkDays"])
              : item?.companyAccountTnAConfig
              ? convertBooleanToYesOrNo(item.companyAccountTnAConfig["vacationExcludesOffScheduleWorkDays"])
              : item?.legislationTnAConfig
              ? convertBooleanToYesOrNo(item.legislationTnAConfig["vacationExcludesOffScheduleWorkDays"])
              : item?.allLegislationsTnAConfig
              ? convertBooleanToYesOrNo(item.allLegislationsTnAConfig["vacationExcludesOffScheduleWorkDays"])
              : " ",
          },

          [`${t(`${tRPTnAFields}.vacation_days_added_at`)}`]: {
            value: item.tnAConfig
              ? (item.tnAConfig?.vacationDaysAddedAt?.name ?? "") +
                (item.tnAConfig?.vacationDaysAddedAt?.id === VACATION_ADDED_ON_BEGINNING_OF_THE_YEAR_ID
                  ? `: ${t(
                      `${tRPCal}.month_${
                        item.tnAConfig?.vacationDaysAddedOn + 1 < 10
                          ? `0${item.tnAConfig?.vacationDaysAddedOn + 1}`
                          : item.tnAConfig?.vacationDaysAddedOn + 1
                      }`
                    )}`
                  : " ")
              : item.employeeGroupTnAConfig
              ? (item.employeeGroupTnAConfig?.vacationDaysAddedAt?.name ?? "") +
                (item.employeeGroupTnAConfig?.vacationDaysAddedAt?.id === VACATION_ADDED_ON_BEGINNING_OF_THE_YEAR_ID
                  ? `: ${t(
                      `${tRPCal}.month_${
                        item.employeeGroupTnAConfig?.vacationDaysAddedOn + 1 < 10
                          ? `0${item.employeeGroupTnAConfig?.vacationDaysAddedOn + 1}`
                          : item.employeeGroupTnAConfig?.vacationDaysAddedOn + 1
                      }`
                    )}`
                  : " ")
              : item.legalEntityTnAConfig
              ? (item.legalEntityTnAConfig?.vacationDaysAddedAt?.name ?? "") +
                (item.legalEntityTnAConfig?.vacationDaysAddedAt?.id === VACATION_ADDED_ON_BEGINNING_OF_THE_YEAR_ID
                  ? `: ${t(
                      `${tRPCal}.month_${
                        item.legalEntityTnAConfig?.vacationDaysAddedOn + 1 < 10
                          ? `0${item.legalEntityTnAConfig?.vacationDaysAddedOn + 1}`
                          : item.legalEntityTnAConfig?.vacationDaysAddedOn + 1
                      }`
                    )}`
                  : " ")
              : item.companyAccountTnAConfig
              ? (item.companyAccountTnAConfig?.vacationDaysAddedAt?.name ?? "") +
                (item.companyAccountTnAConfig?.vacationDaysAddedAt?.id === VACATION_ADDED_ON_BEGINNING_OF_THE_YEAR_ID
                  ? `: ${t(
                      `${tRPCal}.month_${
                        item.companyAccountTnAConfig?.vacationDaysAddedOn + 1 < 10
                          ? `0${item.companyAccountTnAConfig?.vacationDaysAddedOn + 1}`
                          : item.companyAccountTnAConfig?.vacationDaysAddedOn + 1
                      }`
                    )}`
                  : " ")
              : item.legislationTnAConfig
              ? (item.legislationTnAConfig?.vacationDaysAddedAt?.name ?? "") +
                (item.legislationTnAConfig?.vacationDaysAddedAt?.id === VACATION_ADDED_ON_BEGINNING_OF_THE_YEAR_ID
                  ? `: ${t(
                      `${tRPCal}.month_${
                        item.legislationTnAConfig?.vacationDaysAddedOn + 1 < 10
                          ? `0${item.legislationTnAConfig?.vacationDaysAddedOn + 1}`
                          : item.legislationTnAConfig?.vacationDaysAddedOn + 1
                      }`
                    )}`
                  : " ")
              : item.allLegislationsTnAConfig
              ? (item.allLegislationsTnAConfig?.vacationDaysAddedAt?.name ?? "") +
                (item.allLegislationsTnAConfig?.vacationDaysAddedAt?.id === VACATION_ADDED_ON_BEGINNING_OF_THE_YEAR_ID
                  ? `: ${t(
                      `${tRPCal}.month_${
                        item.allLegislationsTnAConfig?.vacationDaysAddedOn + 1 < 10
                          ? `0${item.allLegislationsTnAConfig?.vacationDaysAddedOn + 1}`
                          : item.allLegislationsTnAConfig?.vacationDaysAddedOn + 1
                      }`
                    )}`
                  : " ")
              : " ",
          },

          [`${t(`${tRPTnAConfig}.highlight_when_worked_time_more`)}`]: {
            value: item?.tnAConfig
              ? item.tnAConfig?.workedTimeMoreThan
              : item?.employeeGroupTnAConfig
              ? item.employeeGroupTnAConfig?.workedTimeMoreThan
              : item?.legalEntityTnAConfig
              ? item.legalEntityTnAConfig?.workedTimeMoreThan
              : item?.companyAccountTnAConfig
              ? item.companyAccountTnAConfig?.workedTimeMoreThan
              : item?.legislationTnAConfig
              ? item.legislationTnAConfig?.workedTimeMoreThan
              : item?.allLegislationsTnAConfig
              ? item.allLegislationsTnAConfig?.workedTimeMoreThan
              : " ",
          },

          [`${t(`${tRPTnAConfig}.highlight_when_vacation_balance_more`)}`]: {
            value: item?.tnAConfig
              ? item.tnAConfig?.vacationBalanceMoreThan
              : item?.employeeGroupTnAConfig
              ? item.employeeGroupTnAConfig?.vacationBalanceMoreThan
              : item?.legalEntityTnAConfig
              ? item.legalEntityTnAConfig?.vacationBalanceMoreThan
              : item?.companyAccountTnAConfig
              ? item.companyAccountTnAConfig?.vacationBalanceMoreThan
              : item?.legislationTnAConfig
              ? item.legislationTnAConfig?.vacationBalanceMoreThan
              : item?.allLegislationsTnAConfig
              ? item.allLegislationsTnAConfig?.vacationBalanceMoreThan
              : " ",
          },

          [`${t(`${tRPTnAConfig}.vacation_balance_after_date`)}`]: {
            value:
              !!item?.tnAConfig &&
              Number.isInteger(item?.tnAConfig?.vacationBalanceAfterDay) &&
              item?.tnAConfig?.vacationBalanceAfterDay !== 0 &&
              Number.isInteger(item?.tnAConfig?.vacationBalanceAfterMonth)
                ? `${item.tnAConfig.vacationBalanceAfterDay} ${t(
                    monthsValues.find((month) => month.value === item?.tnAConfig?.vacationBalanceAfterMonth)?.key ?? ""
                  )}`
                : !!item?.employeeGroupTnAConfig &&
                  Number.isInteger(item?.employeeGroupTnAConfig?.vacationBalanceAfterDay) &&
                  item?.employeeGroupTnAConfig?.vacationBalanceAfterDay !== 0 &&
                  Number.isInteger(item?.employeeGroupTnAConfig?.vacationBalanceAfterMonth)
                ? `${item.employeeGroupTnAConfig.vacationBalanceAfterDay} ${t(
                    monthsValues.find(
                      (month) => month.value === item?.employeeGroupTnAConfig?.vacationBalanceAfterMonth
                    )?.key ?? ""
                  )}`
                : !!item?.legalEntityTnAConfig &&
                  Number.isInteger(item?.legalEntityTnAConfig?.vacationBalanceAfterDay) &&
                  item?.legalEntityTnAConfig?.vacationBalanceAfterDay !== 0 &&
                  Number.isInteger(item?.legalEntityTnAConfig?.vacationBalanceAfterMonth)
                ? `${item.legalEntityTnAConfig.vacationBalanceAfterDay} ${t(
                    monthsValues.find((month) => month.value === item?.legalEntityTnAConfig?.vacationBalanceAfterMonth)
                      ?.key ?? ""
                  )}`
                : !!item?.companyAccountTnAConfig &&
                  Number.isInteger(item?.companyAccountTnAConfig?.vacationBalanceAfterDay) &&
                  item?.companyAccountTnAConfig?.vacationBalanceAfterDay !== 0 &&
                  Number.isInteger(item?.companyAccountTnAConfig?.vacationBalanceAfterMonth)
                ? `${item.companyAccountTnAConfig.vacationBalanceAfterDay} ${t(
                    monthsValues.find(
                      (month) => month.value === item?.companyAccountTnAConfig?.vacationBalanceAfterMonth
                    )?.key ?? ""
                  )}`
                : !!item?.legislationTnAConfig &&
                  Number.isInteger(item?.legislationTnAConfig?.vacationBalanceAfterDay) &&
                  item?.legislationTnAConfig?.vacationBalanceAfterDay !== 0 &&
                  Number.isInteger(item?.legislationTnAConfig?.vacationBalanceAfterMonth)
                ? `${item.legislationTnAConfig.vacationBalanceAfterDay} ${t(
                    monthsValues.find((month) => month.value === item?.legislationTnAConfig?.vacationBalanceAfterMonth)
                      ?.key ?? ""
                  )}`
                : !!item?.allLegislationsTnAConfig &&
                  Number.isInteger(item?.allLegislationsTnAConfig?.vacationBalanceAfterDay) &&
                  item?.allLegislationsTnAConfig?.vacationBalanceAfterDay !== 0 &&
                  Number.isInteger(item?.allLegislationsTnAConfig?.vacationBalanceAfterMonth)
                ? `${item.allLegislationsTnAConfig.vacationBalanceAfterDay} ${t(
                    monthsValues.find(
                      (month) => month.value === item?.allLegislationsTnAConfig?.vacationBalanceAfterMonth
                    )?.key ?? ""
                  )}`
                : " ",
          },

          [`${t(`${tRPTnAConfig}.highlight_when_delay_in_signoff_more`)}`]: {
            value: item?.tnAConfig
              ? item.tnAConfig?.delayInSignOffMoreThan
              : item?.employeeGroupTnAConfig
              ? item.employeeGroupTnAConfig?.delayInSignOffMoreThan
              : item?.legalEntityTnAConfig
              ? item.legalEntityTnAConfig?.delayInSignOffMoreThan
              : item?.companyAccountTnAConfig
              ? item.companyAccountTnAConfig?.delayInSignOffMoreThan
              : item?.legislationTnAConfig
              ? item.legislationTnAConfig?.delayInSignOffMoreThan
              : item?.allLegislationsTnAConfig
              ? item.allLegislationsTnAConfig?.delayInSignOffMoreThan
              : " ",
          },

          [`${t(`${tRPTnAConfig}.highlight_when_delay_in_signoff_reminders_more`)}`]: {
            value: item?.tnAConfig
              ? item.tnAConfig?.delayInSendingSignOffMoreThan
              : item?.employeeGroupTnAConfig
              ? item.employeeGroupTnAConfig?.delayInSendingSignOffMoreThan
              : item?.legalEntityTnAConfig
              ? item.legalEntityTnAConfig?.delayInSendingSignOffMoreThan
              : item?.companyAccountTnAConfig
              ? item.companyAccountTnAConfig?.delayInSendingSignOffMoreThan
              : item?.legislationTnAConfig
              ? item.legislationTnAConfig?.delayInSendingSignOffMoreThan
              : item?.allLegislationsTnAConfig
              ? item.allLegislationsTnAConfig?.delayInSendingSignOffMoreThan
              : " ",
          },

          sortDirection: headerNames,
        };
      });
    },
    [headerNames]
  );

  const exportToExcel = useCallback(
    async (option: "exportAllData" | "exportCurrent" = "exportCurrent") => {
      if (option === "exportCurrent") {
        const employeeGroup = employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        exportTnAToExcel(
          dataToExportAsExcel(allItems()),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${!legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""} ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } Rules`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }

      if (option === "exportAllData") {
        const employeeGroup = employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        const firstResponse = await loadTnARulesData();

        if (firstResponse?.error) return;

        const allTnARulesData = await loadTnARulesData(firstResponse?.totalItems);

        if (!!allTnARulesData?.error || !allTnARulesData?.items) return;

        exportTnAToExcel(
          dataToExportAsExcel(allTnARulesData.items),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${!legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""} ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } Rules`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }
    },
    [
      rowsBeforeHeaderInExcel,
      loadEmployeeGroup,
      headerNames,
      dataToExportAsExcel,
      employeeGroupOption?.employeeGroupIds,
      loadTnARulesData,
    ]
  );

  return (
    <ExportToExcelDialog
      handleExportToExcel={exportToExcel}
      disabled={disabled}
    />
  );
};

export default ExportTnARulesToExcel;
